// src/app/components/MultiplacerCeilingDevices/SingleDevice.tsx
import React, { useCallback, useMemo } from "react";
import { ActionIcon, Group, NumberInput, Stack, Text, Tooltip } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  copyDeviceThunk,
  selectActiveDeviceInSelectedRoom,
  selectActiveDeviceIsAirTerminal,
  rotateDeviceThunk,
} from "../../DesignTab/devices/deviceSlice";
import { selectHttpGetDeviceTypes } from "../../DesignTab/deviceTypesSelectors";
import { DeviceTypeMenu } from "../../components/DeviceTypeMenu";
import type { IDeviceType } from "@backend/src/schemas";
import { selectActiveRoomIsLocked } from "../../Room/roomSelectors";
import {
  useDeleteMultiplacerDeviceDeleteDeviceMutation,
  usePutMultiplacerDeviceUpdateMutation,
} from "../../../openapi";
import { FaTrash, FaCopy } from "react-icons/fa";
import { MdOutlineTurnLeft, MdOutlineTurnSlightLeft } from "react-icons/md";
import { useDebouncedCallback } from "use-debounce";

export const SingleDevice: React.FC = () => {
  const dispatch = useAppDispatch();
  const device = useAppSelector(selectActiveDeviceInSelectedRoom);
  const deviceTypes = useAppSelector(selectHttpGetDeviceTypes);
  const isAirTerminal = useAppSelector(selectActiveDeviceIsAirTerminal);
  const roomIsLocked = useAppSelector(selectActiveRoomIsLocked);

  const [deleteSingleDevice] = useDeleteMultiplacerDeviceDeleteDeviceMutation();
  const [updateDevice] = usePutMultiplacerDeviceUpdateMutation();

  const sameCategoryTypes = useMemo(
    () => (device ? deviceTypes.filter((dt) => dt.deviceCategory === device.deviceCategory) : []),
    [device, deviceTypes]
  );

  const selectedType: IDeviceType | null = useMemo(
    () =>
      device ? (sameCategoryTypes.find((dt) => dt.compCode === device.compCode) ?? null) : null,
    [device, sameCategoryTypes]
  );

  const changeTypeOfProduct = useCallback(
    (dt: IDeviceType) => {
      if (dt.compCode == null || device == null) return;
      const deviceType = deviceTypes?.find((t) => t.compCode === dt.compCode);

      updateDevice({
        body: {
          device: {
            ...device,
            compCode: dt.compCode,
            description: deviceType?.typeName ?? "",
          },
        },
      });
    },
    [device, deviceTypes, updateDevice]
  );

  const handleCopy = useCallback(() => {
    if (!device) return;
    dispatch(copyDeviceThunk(device._id));
  }, [device, dispatch]);

  const handleDelete = useCallback(() => {
    if (!device) return;
    deleteSingleDevice({ deviceId: device._id });
  }, [device, deleteSingleDevice]);

  if (!device) {
    return (
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Valgt komponent
        </Text>
        <Text size="sm" c="dimmed">
          Ingen komponent er valgt. Klikk på et komponent i listen for å redigere.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">
          Valgt komponent
        </Text>

        <Group gap="xs">
          {/* Delete */}
          <Tooltip label="Slett komponent">
            <ActionIcon
              variant="outline"
              color="red"
              disabled={roomIsLocked}
              onClick={handleDelete}
            >
              <FaTrash size={12} />
            </ActionIcon>
          </Tooltip>

          {/* Copy */}
          <Tooltip label="Kopier komponent">
            <ActionIcon variant="outline" color="blue" disabled={roomIsLocked} onClick={handleCopy}>
              <FaCopy size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Typväljare */}
      <DeviceTypeMenu
        disabled={roomIsLocked}
        deviceCategory={device.deviceCategory}
        deviceTypes={sameCategoryTypes}
        selectedType={selectedType}
        onSelect={changeTypeOfProduct}
      />

      {isAirTerminal && <AirDevice />}
    </Stack>
  );
};

export default SingleDevice;

const AirDevice: React.FC = () => {
  const [updateDevice] = usePutMultiplacerDeviceUpdateMutation();
  const dispatch = useAppDispatch();

  const roomIsLocked = useAppSelector(selectActiveRoomIsLocked);
  const device = useAppSelector(selectActiveDeviceInSelectedRoom);

  const debouncedUpdateDevice = useDebouncedCallback(
    (value: number, supplyOrReturn: "SUPPLY" | "RETURN") => {
      if (!device) return;

      const objToUpdate =
        supplyOrReturn === "SUPPLY"
          ? { supplyAirFlow: Number(value) }
          : { returnAirFlow: Number(value) };

      updateDevice({
        body: {
          device: {
            ...device,
            ...objToUpdate,
          },
        },
      });
    },
    1000
  );

  const isSupply = useMemo(
    () => device?.deviceCategory === "SUPPLY_AIR_DEVICE",
    [device?.deviceCategory]
  );
  const isReturn = useMemo(
    () => device?.deviceCategory === "RETURN_AIR_DEVICE",
    [device?.deviceCategory]
  );

  if (!device) {
    return null;
  }

  return (
    <Stack gap="md">
      {isSupply && (
        <NumberInput
          w={120}
          disabled={roomIsLocked}
          label="Mengde (m³/h)"
          placeholder="Angi luftmengde"
          onChange={(value) => debouncedUpdateDevice(Number(value) ?? 0, "SUPPLY")}
          value={device.supplyAirFlow ?? 0}
        />
      )}

      {isReturn && (
        <NumberInput
          w={120}
          disabled={roomIsLocked}
          label="Mengde (m³/h)"
          placeholder="Angi luftmengde"
          onChange={(value) => debouncedUpdateDevice(Number(value) ?? 0, "RETURN")}
          value={device.returnAirFlow ?? 0}
        />
      )}

      {isSupply && (
        <Group gap="xs" align="center">
          <Text size="sm">Retning</Text>

          <Tooltip label="Roter 90°">
            <ActionIcon
              disabled={roomIsLocked}
              variant="outline"
              color="blue"
              size="sm"
              onClick={() =>
                dispatch(
                  rotateDeviceThunk({
                    device,
                    angle: 90,
                  })
                )
              }
            >
              <MdOutlineTurnLeft size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Roter 45°">
            <ActionIcon
              disabled={roomIsLocked}
              variant="outline"
              color="blue"
              size="sm"
              onClick={() =>
                dispatch(
                  rotateDeviceThunk({
                    device,
                    angle: 45,
                  })
                )
              }
            >
              <MdOutlineTurnSlightLeft size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
};
